import { action, computed, observable } from 'mobx';
import { cloneDeep, get, sortBy, uniq } from 'lodash';
import moment from 'moment';
import api from 'core/util/api';
import $devices from 'app/stores/device/$devices';
import { closestLimitCountOptionValue, closestWindowSizeOptionValue } from 'app/views/metrics/utils/sidebarOptions.js';
import { METRICS_APP_PROTOCOL_MAP } from 'app/stores/metrics/metricsConstants';
import { getHashForObject, getObjectForHash } from 'app/stores/query/urlHash';
import { DEFAULT_METRICS_EXPLORER_QUERY } from 'app/views/metrics/queries';
import { modelFinder } from 'app/stores/util/modelFinder';
import { getNmsDimensionLabel } from 'app/util/dimensions';
import { AutoUpdate } from 'app/util/nms/metricsExplorer';
import { DimensionUsage } from 'app/util/nms/metrics';
import {
  exportBgpNeighborsForDeviceCsv,
  exportComponentsForDeviceCsv,
  exportCsv,
  exportFileName,
  exportInterfacesForDeviceCsv,
  exportQueryResultsCollectionTimeseriesToCsv,
  exportQueryResultsCollectionToCsv,
  exportRemoteCsv
} from 'app/stores/metrics/metricsExports';
import { fetchMetricsInterface } from 'app/stores/metrics/metricsInterfaces';
import MetricsCollection from './MetricsCollection';
import MetricDeviceCollection from './MetricDeviceCollection';
import MetricDeviceModel from './MetricDeviceModel';
import MonitoringTemplatesCollection from './MonitoringTemplatesCollection';
import CustomReportsCollection from './CustomReportsCollection';
import {
  fetchAsnsWithAsGroups,
  fetchDevice,
  fetchDeviceBgpData,
  fetchDeviceFilterOptions,
  fetchDevicesAvailability,
  fetchDeviceUptime,
  fetchInterfaceFilterOptions,
  getMetricValueLabel,
  processTopologyDataForDevice,
  saveInterfaceConnection
} from './metricsApi';

export class MetricsStore {
  deviceCollection = new MetricDeviceCollection();

  availableMetricsCollection = new MetricsCollection();

  monitoringTemplatesCollection = new MonitoringTemplatesCollection();

  customReportsCollection = new CustomReportsCollection();

  @observable.ref
  initializePromise;

  @action
  fetch() {}

  @action
  initialize() {
    if (!this.initializePromise) {
      const { $auth } = this.store;
      if ($auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
        // on shared links this is served from the measurements saved on the link (see app/util/sharedLinkLookups)
        this.initializePromise = this.availableMetricsCollection.fetch();
      }
    }
    return this.initializePromise || Promise.resolve();
  }

  @action
  destroy() {
    this.initializePromise = null;
  }

  @computed
  get entityTypeValues() {
    const { entityTypes } = this.availableMetricsCollection;
    return Object.keys(entityTypes);
  }

  @computed
  get entityTypeOptions() {
    const { entityTypes } = this.availableMetricsCollection;
    return Object.values(entityTypes).sort((a, b) => a.label.localeCompare(b.label));
  }

  getEntityTypeMeasurementOptions = (entityType) => {
    const { entityTypes } = this.availableMetricsCollection;
    const entityTypeMatch = entityTypes[entityType];

    if (!entityTypeMatch) {
      return [];
    }

    return Object.values(entityTypeMatch.measurements)
      .map(({ name }) => ({ label: name, value: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  getEntityTypeDimensionOptions = (entityType, measurementKeys = [], options = {}) => {
    const { showHeadings } = options;
    const { entityTypes } = this.availableMetricsCollection;
    const entityTypeMatch = entityTypes[entityType];

    if (!entityTypeMatch || !Array.isArray(measurementKeys)) {
      return [];
    }

    const dimensionOptions = [];

    measurementKeys.forEach((measurementKey) => {
      const dimensionsForMeasurement = entityTypeMatch.measurements[measurementKey]?.dimensions || [];

      if (showHeadings) {
        const headingValue = dimensionsForMeasurement
          .map((o) => `${o.label}${o.value}`)
          .join('')
          .replaceAll(measurementKey, '');

        dimensionOptions.push({
          label: measurementKey,
          // The huge value is because we want this heading to appear whenever there are matches under it,
          // otherwise the heading will be filtered out and we lose the measurement context.
          value: `${measurementKey}-${headingValue}`,
          groupHeaderProps: {
            bg: 'transparent',
            mx: '4px',
            my: '4px',
            py: '4px',
            px: 0,
            borderBottom: 'thin',
            fontWeight: 'bold',
            borderRadius: 0
          }
        });
      }

      dimensionOptions.push(...dimensionsForMeasurement);
    });

    if (measurementKeys.length > 1) {
      return dimensionOptions.map((dimension) => ({
        ...dimension,
        label: `${dimension.label}`
      }));
    }

    return dimensionOptions;
  };

  queryToHash(query, options = {}) {
    return getHashForObject(query, null, options);
  }

  /**
   * This is only private to make test mocks easier.
   * @returns {function(ip: string): import('./MetricDeviceModel').default | undefined}
   * @private
   */
  @computed
  get _byIp() {
    return modelFinder(this.deviceCollection, 'ip_address');
  }

  byIp(ip_address) {
    return this._byIp(ip_address);
  }

  /**
   * This is only private to make test mocks easier.
   * @returns {function(macAdress: string): import('./MetricDeviceModel').default | undefined}
   * @private
   */
  @computed
  get _byMacAddress() {
    return modelFinder(this.deviceCollection, 'mac_address');
  }

  byMacAddress(mac_address) {
    return this._byMacAddress(mac_address);
  }

  /**
   * @param {string} [device_name]
   * @param {string} [device_ip]
   * @param {string} [mac_address]
   * @returns {string | undefined}
   */
  getMetricsDeviceNameFromQueryModel({ device_name, device_ip, mac_address }) {
    if (!device_name && device_ip) {
      device_name = this.byIp(device_ip)?.get('name');
    }
    if (!device_name && mac_address) {
      device_name = this.byMacAddress(mac_address)?.get('name');
    }
    return device_name;
  }

  queryTitle(query) {
    if (!query) {
      return '';
    }
    const { selectedMeasurement, selectedMetrics, selectedDimensions } = this.queryToFormValues(query);
    const measurement = this.measurementModelById(selectedMeasurement);
    const metrics = selectedMetrics.map((m) => measurement?.anyLabel(m)).join(', ');
    const dimensions = selectedDimensions.map((m) => measurement?.anyLabel(m)).join(', ');
    return `Top ${dimensions} by ${metrics} `;
  }

  hashToQuery(hash) {
    return Promise.resolve()
      .then(() => {
        if (hash) {
          return getObjectForHash(hash).then((values) => ({ values, urlHash: hash }));
        }

        const query = DEFAULT_METRICS_EXPLORER_QUERY;
        return this.queryToHash(query).then((urlHash) => ({ values: query, urlHash }));
      })
      .then(({ values, urlHash }) => {
        let { query, title } = values;

        if (values.status && values.statusCode) {
          console.warn('ME queryHash not found, using default', hash);
          query = DEFAULT_METRICS_EXPLORER_QUERY;
          values = { ...this.queryToFormValues(query), query };
        }
        // In case you're coming from a real query
        else if (!values.selectedMeasurement && values.kmetrics) {
          query = values;
          values = { ...this.queryToFormValues(query), query };
        }

        // if spoofing from another company, get new ID of measurement (if it exists)
        if (!this.measurementModelById(values.selectedMeasurement)) {
          const measurement = this.measurementModelByName(values.query.kmetrics.measurement);

          if (measurement) {
            values.selectedMeasurement = measurement.id;
          }
        }

        if (!title) {
          title = this.getAutoQueryTitle(values);
        }

        return { values: { ...values, query, title }, urlHash };
      });
  }

  getAutoQueryTitle({ selectedMeasurement, selectedMetrics, selectedDimensions }) {
    const measurement = this.measurementModelById(selectedMeasurement);
    const metrics = selectedMetrics.map((m) => measurement?.anyLabel(m)).join(', ');
    const dimensions = selectedDimensions.map((m) => measurement?.anyLabel(m)).join(', ');

    return `Top ${dimensions} by ${metrics} `;
  }

  get vizTypes() {
    return {
      line: {
        label: 'Line',
        category: 'timeSeries',
        component: '',
        icon: 'timeline-line-chart'
      },
      area: {
        label: 'Area',
        category: 'timeSeries',
        component: '',
        icon: 'timeline-area-chart'
      },
      column: {
        label: 'Column',
        category: 'agg',
        component: '',
        icon: 'grouped-bar-chart'
      },
      pie: {
        label: 'Pie',
        category: 'agg',
        component: '',
        icon: 'pie-chart'
      },
      table: {
        label: 'Table Only',
        category: 'agg',
        component: '',
        icon: 'th-list'
      }
      /* ,
      pieCount: {
        label: 'Count by value',
        category: 'countBy',
        component: '',
        icon: 'calculator'
      } */
    };
  }

  getStorageMetrics = ({ measurement, measurementModel }) => {
    const model = measurementModel || this.measurementModel(measurement);

    if (!model) {
      return null;
    }

    return model.get('storage.Metrics');
  };

  getStorageMetricLabel = ({ metric, measurement, measurementModel }) => {
    const metricsStorage = this.getStorageMetrics({ measurement, measurementModel });
    return metricsStorage?.[metric]?.Label || metric;
  };

  /**
   * @deprecated import directly from metricsApi instead
   */
  getMetricValueLabel = getMetricValueLabel;

  vizTypeOptions = (category) => {
    let vizOptions = Object.keys(this.vizTypes).map((vizType) => ({
      value: vizType,
      label: this.vizTypes[vizType].label,
      icon: this.vizTypes[vizType].icon
    }));
    if (category) {
      vizOptions = vizOptions.filter((opt) => this.vizTypes[opt.value].category === category);
    }
    return vizOptions;
  };

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchDevice = fetchDevice;

  async fetchDeviceFromCollection(deviceId) {
    let device = this.deviceCollection.get(deviceId);

    if (!device) {
      device = new MetricDeviceModel({ id: deviceId });
      this.deviceCollection.add(device);
    }

    await device.fetch();
    return device;
  }

  /**
   * @deprecated import directly from metricsApi instead
   */
  processTopologyDataForDevice = processTopologyDataForDevice;

  /**
   * @deprecated import directly from metricsApi instead
   */
  saveInterfaceConnection = saveInterfaceConnection;

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchDeviceFilterOptions = fetchDeviceFilterOptions;

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchInterfaceFilterOptions = fetchInterfaceFilterOptions;

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchDevicesAvailability = fetchDevicesAvailability;

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchDeviceBgpData = fetchDeviceBgpData;

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchAsnsWithAsGroups = fetchAsnsWithAsGroups;

  /**
   * @deprecated import directly from metricsInterfaces instead
   */
  fetchInterface = fetchMetricsInterface;

  /**
   *
   * This gets called from the Add ICMP Devices page, but re-use the same API
   * endpoint as the Discovery page.
   */
  @action
  createIcmpDevices = (configs, collection) =>
    api
      .post('/api/ui/discovery/devices', {
        body: { devices: configs }
      })
      .then((results) => {
        const hasErrors = results.some((result) => result.success === false);
        const failedDevices = [];

        if (hasErrors) {
          results.forEach((result) => {
            const device = collection.models.find((d) => d.get('address') === result.address);

            if (device) {
              device.set(result);

              if (result.success === false) {
                failedDevices.push(device);
              }
            }
          });

          collection.selected = failedDevices;

          return Promise.reject();
        }

        return this.deviceCollection
          .fetch({ force: true })
          .then(() => $devices.loadDeviceSummaries())
          .then(() => results);
      });

  /**
   * @deprecated import directly from metricsApi instead
   */
  fetchDeviceUptime = fetchDeviceUptime;

  getFullSelectedMeasurement(selectedMeasurement) {
    return this.availableMeasurementOptions.find(
      (measurement) => measurement.id === selectedMeasurement || measurement.measurement === selectedMeasurement
    );
  }

  /**
   * Returns a list of measurement info formatted for form options for the list of provided measurement names.
   * The returned options have the measurement name (not ID) as the option value.
   */
  getMeasurementOptionsFromMeasurementNames(measurementNames) {
    return this.availableLabelMeasurementOptions.filter((option) => measurementNames?.includes(option.value));
  }

  getAvailableMetricOptions(measurement) {
    if (!measurement) {
      return [];
    }

    const measurementModel = this.measurementModel(measurement);

    if (!measurementModel) {
      return [];
    }

    const metrics = measurementModel.get('storage.Metrics', {});

    return Object.keys(metrics)
      .map((key) => {
        const metric = metrics[key];
        const rawMetricType = metric.Type;
        const metricKind = metric.Kind || 'gauge';

        return {
          value: key,
          label: metric.Label || key,
          type: rawMetricType,
          kind: metricKind,
          unit: metric.Unit,
          values: metric.Values
        };
      })

      .sort((a, b) => a.label.localeCompare(b.label));
  }

  getAvailableDimensionOptions(
    measurement,
    usage = DimensionUsage.Query,
    { prefixDimensionsWithMeasurement = false } = {}
  ) {
    if (!measurement) {
      return [];
    }

    if (!Object.values(DimensionUsage).includes(usage)) {
      // Incompatible usage arg received
      return [];
    }

    const measurementModel = this.measurementModel(measurement);

    if (!measurementModel) {
      return [];
    }

    const dimensions = measurementModel.get('storage.Dimensions', {});

    return Object.keys(dimensions)
      .map((key) => {
        const dimension = dimensions[key];
        let isAvailableDimension = false;
        switch (usage) {
          case DimensionUsage.Query:
            isAvailableDimension = dimension?.Usage?.query;
            break;
          case DimensionUsage.NmsAlertPolicy:
            isAvailableDimension = dimension?.Usage?.kmetricsAlert;
            break;
          case DimensionUsage.FlowAlertPolicy:
            isAvailableDimension = dimension?.Usage?.flowAlert;
            break;
          default:
          // should never get here, but let isAvailableDimension stay false
        }
        return isAvailableDimension
          ? {
              label: getNmsDimensionLabel(key, dimension),
              value: `${prefixDimensionsWithMeasurement ? `${measurementModel.get('measurement')},` : ''}${key}`,
              type: dimension?.Type
            }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  getAvailableQueryDimensionOptions(measurement, options) {
    return this.getAvailableDimensionOptions(measurement, DimensionUsage.Query, options);
  }

  getAvailableNmsPolicyDimensionOptions(measurement, options) {
    return this.getAvailableDimensionOptions(measurement, DimensionUsage.NmsAlertPolicy, options);
  }

  getAvailableFlowPolicyDimensionOptions(measurement, options) {
    return this.getAvailableDimensionOptions(measurement, DimensionUsage.FlowAlertPolicy, options);
  }

  getAvailableDimensionFilterOptions(measurement, usage = DimensionUsage.Query) {
    if (Array.isArray(measurement)) {
      const dimensions = measurement.map((m) => this.getAvailableDimensionOptions(m, usage));

      return dimensions.flat().sort((a, b) => a.label.localeCompare(b.label));
    }

    // Include dimensions that we want selectable in filters, but not as metric dimensions
    return this.getAvailableDimensionOptions(measurement, usage).sort((a, b) => a.label.localeCompare(b.label));
  }

  getAvailableQueryDimensionFilterOptions(measurement) {
    return this.getAvailableDimensionFilterOptions(measurement, DimensionUsage.Query);
  }

  getAvailableNmsPolicyDimensionFilterOptions(measurement) {
    return this.getAvailableDimensionFilterOptions(measurement, DimensionUsage.NmsAlertPolicy);
  }

  getAvailableFlowPolicyDimensionFilterOptions(measurement) {
    return this.getAvailableDimensionFilterOptions(measurement, DimensionUsage.FlowAlertPolicy);
  }

  metricFilterOptionsFromQuery(query) {
    const measurement = this.getFullSelectedMeasurement(query.kmetrics.measurement).id;
    const rollups = Object.values(query.kmetrics.rollups).map((r) => r.aggregate);
    const metrics = Object.values(query.kmetrics.rollups).map((r) => r.metric);
    return this.metricFilterOptions(measurement, rollups, metrics);
  }

  metricFilterOptions(measurement, rollups, metrics) {
    const allMetrics = this.getAvailableMetricOptions(measurement);
    const options = [];
    metrics.forEach((metric) => {
      rollups.forEach((agg) => {
        const fullMetric = allMetrics.find((m) => m.value === metric);
        const { label, values, type } = fullMetric;
        options.push({
          value: `${agg}_${metric}`,
          label: `${label} (${agg})`,
          type,
          values
        });
      });
    });
    return options;
  }

  // Omitting column gives you a prefix
  getAppProtocolDimension(measurementName, column = '', measurementModel) {
    const model = measurementModel || this.measurementModel(measurementName);
    let appProtocolName;

    if (model) {
      const appProtocolNum = model.get('storage.AppProtocol');
      appProtocolName = METRICS_APP_PROTOCOL_MAP[appProtocolNum];

      // If it's an uncommon case, check dictionary
      if (appProtocolNum && !appProtocolName) {
        const appProtocolDictionary = this.store.$dictionary.get('app_protocols', {});
        appProtocolName = Object.keys(appProtocolDictionary).find(
          (key) => appProtocolDictionary[key]?.id === appProtocolNum
        );
      }
    }
    return `ktappprotocol__${appProtocolName || 'kmetrics'}__${column || ''}`;
  }

  measurementModel = (measurement) =>
    // Accepts dimension name or ID.
    this.measurementModelById(measurement) || this.measurementModelByName(measurement);

  measurementModelById = (measurement) => this.availableMetricsCollection.modelById[measurement];

  measurementModelByName = (measurement) =>
    this.availableMetricsCollection.models.find((model) => model.get('measurement') === measurement);

  deviceModelByName = (deviceName) =>
    this.deviceCollection.models.find((model) => {
      const modelDeviceName = `${model.get('name')}`.toLowerCase();
      const matchDeviceName = `${deviceName}`.toLowerCase();
      return modelDeviceName === matchDeviceName;
    });

  formValuesToQuery = (formValues, options, overrides) => {
    const {
      selectedMeasurement,
      selectedMetrics,
      selectedDimensions,
      selectedMergeDimensions,
      selectedMergeAggregation,
      selectedSortOrder,
      selectedLimitCount,
      selectedAggFunc,
      selectedWindowSize,
      selectedTransformation,
      selectedRollupsLimit,
      selectedRollupsAggFunc,
      selectedVisualization,
      selectedVisualizationMetric,
      selectedVisualizationRollup,
      filters,
      rollupFilters,
      streamingUpdate,
      lookback_seconds,
      ending_time,
      starting_time,
      update_frequency,
      zeroFill
    } = formValues;

    const { allowMultiAggregate } = options || {};

    const measurement = this.measurementModelById(selectedMeasurement)?.label;

    let rangeObject = {};
    if (starting_time && ending_time) {
      rangeObject = {
        start: starting_time,
        end: ending_time,
        lookback_seconds: 0
      };
    } else if (lookback_seconds) {
      rangeObject = {
        lookback: `PT${lookback_seconds}S` // start, end
      };
    }

    const rollups = {};
    const fn = {};

    selectedMetrics.forEach((metric) => {
      fn[metric] = selectedAggFunc;
      const selectedRollupsAggFuncs = Array.isArray(selectedRollupsAggFunc)
        ? selectedRollupsAggFunc
        : [selectedRollupsAggFunc];

      (allowMultiAggregate ? selectedRollupsAggFuncs : selectedRollupsAggFuncs.slice(0, 1)).forEach((aggFunc) => {
        rollups[`${aggFunc}_${metric}`] = {
          metric,
          aggregate: aggFunc
        };
      });
    });

    // if a Table is request, we don't need timeseries data so set the limit to 0
    const includeTimeseries = selectedVisualization === 'table' ? 0 : selectedLimitCount;

    // move selectedVisualizationMetric to the top so it's sorted first
    const rollupKeys = sortBy(Object.keys(rollups), (rollup) =>
      rollups[rollup].metric === selectedVisualizationMetric ? 0 : 1
    );
    const sort = rollupKeys.map((rollup) => ({
      name: rollup,
      direction: selectedSortOrder
    }));

    const query = {
      update_frequency,
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement,
        dimensions: selectedDimensions,

        metrics: selectedMetrics.map((metric) => ({
          name: metric,
          type: selectedTransformation === 'none' ? 'gauge' : 'counter'
        })),

        range: rangeObject,

        // sum, min, max, avg
        merge: { dimensions: selectedMergeDimensions, aggregate: selectedMergeAggregation },

        window: {
          size: selectedWindowSize,
          fn
        },
        rollups,
        sort,
        limit: selectedRollupsLimit,
        includeTimeseries,
        streamingUpdate,
        viz: {
          type: selectedVisualization,
          rollup: selectedVisualizationRollup,
          metric: selectedVisualizationMetric
        },
        zeroFill
      }
    };

    // To get raw, non-bucketized metric results, just omit the window section from the query object.
    if (selectedAggFunc === 'none') {
      delete query.kmetrics.window;
    }

    if (!selectedMergeDimensions?.length) {
      delete query.kmetrics.merge;
    }

    // can't send in an empty filters object, so check if we have any set before add to the query
    if (filters && filters.filterGroups.length > 0) {
      query.kmetrics.filters = filters;
    } else {
      delete query.kmetrics.filters;
    }

    if (rollupFilters && rollupFilters.filterGroups.length > 0) {
      query.kmetrics.rollupFilters = rollupFilters;
    } else {
      delete query.kmetrics.rollupFilters;
    }

    if (overrides) {
      query.kmetrics = {
        ...query.kmetrics,
        ...overrides
      };
    }

    return query;
  };

  queryToFormValues = (query) => {
    const { kmetrics, time_format = this.store.$auth.userTimezone || 'UTC', update_frequency } = query;

    const {
      measurement,
      dimensions,
      filters,
      metrics,
      range,
      window: tsWindow,
      rollups,
      rollupFilters,
      sort,
      limit,
      includeTimeseries,
      streamingUpdate,
      merge,
      viz,
      zeroFill
    } = kmetrics;
    let autoUpdate = AutoUpdate.Off;
    if (update_frequency) {
      autoUpdate = AutoUpdate.Periodic;
    } else if (streamingUpdate) {
      autoUpdate = AutoUpdate.Streaming;
    }
    const {
      type: selectedVisualization,
      rollup: selectedVisualizationRollup,
      metric: selectedVisualizationMetric
    } = viz || {};
    const { lookback, start: starting_time, end: ending_time } = range;
    const formFields = {
      selectedMeasurement: this.measurementModelByName(measurement).id,
      selectedDimensions: dimensions,
      selectedMergeDimensions: merge?.dimensions,
      selectedMergeAggregation: merge?.aggregate,
      selectedMetrics: metrics.map((metric) => metric.name),
      selectedVisualization,
      selectedVisualizationRollup,
      selectedVisualizationMetric,
      selectedTransformation: metrics[0]?.type === 'gauge' ? 'none' : 'counter',
      selectedSortOrder: get(sort, '[0].direction', 'desc'),
      selectedRollupsLimit: closestLimitCountOptionValue(limit),
      selectedRollupsAggFunc: uniq(Object.values(rollups).map((rollup) => rollup.aggregate)),
      selectedLimitCount: closestLimitCountOptionValue(includeTimeseries),
      lookback_seconds: starting_time && ending_time ? 0 : moment.duration(lookback).asSeconds(),
      from_to_lookback: 0,
      time_format,
      starting_time,
      ending_time,
      period_over_period: '',
      period_over_period_lookback: '',
      period_over_period_lookback_unit: '',
      use_alt_timestamp_field: '',
      filters,
      rollupFilters,
      streamingUpdate,
      update_frequency,
      autoUpdate,
      zeroFill
    };
    if (tsWindow?.size) {
      formFields.selectedWindowSize = closestWindowSizeOptionValue(tsWindow.size);
    }
    if (tsWindow?.fn) {
      [formFields.selectedAggFunc] = Object.values(tsWindow.fn);
    } else {
      formFields.selectedAggFunc = 'none';
    }
    return formFields;
  };

  dimensionFilterTransformer = (filters, measurement, isKdeToRecon) => {
    // Transforms recon names to KDE columns and vice versa.
    const measurementModel = this.measurementModel(measurement);
    const dimensionPrefix = this.getAppProtocolDimension(measurement);
    const dimensions = measurementModel?.get('storage.Dimensions');
    const metrics = measurementModel?.get('storage.Metrics');

    const merged = {
      ...dimensions,
      ...metrics,
      km_measurement_name: { name: 'km_measurement_name', Column: 'km_measurement_name', Label: 'Measurement' },
      km_device_id: { name: 'km_device_id', Column: 'km_device_id', Label: 'Device ID' }
    };

    // Clone filterObject because traverse mutates object.
    const filtersCopy = cloneDeep(filters);

    function traverse(object) {
      for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key) && object[key] && typeof object[key] === 'object') {
          traverse(object[key]);
        } else if (key === 'filterField') {
          if (isKdeToRecon && String(object[key]).startsWith(dimensionPrefix)) {
            const kdeColumn = object[key].replace(dimensionPrefix, '');
            const name = measurementModel?.mergedMetricsDimensions[kdeColumn]?.name;

            if (name) {
              object[key] = name;
            }
          } else if (!isKdeToRecon && merged[object[key]]) {
            const { Column, QueryColumn } = merged[object[key]];
            object[key] = `${dimensionPrefix}${Column || QueryColumn}`;
          }
        }
      }
    }

    traverse(filtersCopy);
    return filtersCopy;
  };

  dimensionFilterKdeToReconTransformer = (filters, measurement) =>
    this.dimensionFilterTransformer(filters, measurement, true);

  dimensionFilterReconToKdeTransformer = (filters, measurement) =>
    this.dimensionFilterTransformer(filters, measurement, false);

  // Get full details for a metric identified by measurement and metric IDs
  metricDetails(measurement, metric) {
    const measurementDetails = this.getFullSelectedMeasurement(measurement);
    if (!measurementDetails) {
      return null;
    }
    return measurementDetails.storage?.Metrics?.[metric];
  }

  // Does the selected metric have an enumerated set of possible Values?
  metricIsEnumType = (measurement, metric) => {
    const metricDetails = this.metricDetails(measurement, metric);
    return Boolean(metricDetails?.Values);
  };

  getDimensionLabelForPolicy = (policyModel, dimension) => {
    const selectedMeasurement = policyModel.get('uiProperties.selectedMeasurement');

    return this.getDimensionForMeasurement(selectedMeasurement, dimension)?.Label;
  };

  getDimensionForMeasurement = (measurement, dimension) => {
    const strippedDimension = dimension.replace(this.getAppProtocolDimension(measurement), '');
    const measurementModel = this.measurementModel(measurement);
    return measurementModel?.mergedMetricsDimensions?.[strippedDimension];
  };

  getFullMetricsQuery = ({ metrics, queryOptions, measurement, allowMultiAggregate = true, deckQueryOverrides }) => {
    const {
      selectedDimensions,
      selectedMergeDimensions,
      selectedMergeAggregation,
      selectedSortOrder,
      selectedLimitCount,
      selectedAggFunc,
      selectedWindowSize,
      selectedTransformation,
      selectedRollupsLimit,
      selectedRollupsAggFunc,
      selectedVisualization,
      selectedVisualizationMetric,
      selectedVisualizationRollup,
      filters,
      rollupFilters,
      streamingUpdate,

      // time fields
      lookback_seconds,
      ending_time,
      starting_time
    } = queryOptions;

    let rangeObject = {};
    if (starting_time && ending_time) {
      rangeObject = {
        start: starting_time,
        end: ending_time,
        lookback_seconds: 0
      };
    } else if (lookback_seconds) {
      rangeObject = {
        lookback: `PT${lookback_seconds}S` // start, end
      };
    }

    const rollups = {};
    const fn = {};

    metrics.forEach((metric) => {
      fn[metric] = selectedAggFunc;
      const selectedRollupsAggFuncs = Array.isArray(selectedRollupsAggFunc)
        ? selectedRollupsAggFunc
        : [selectedRollupsAggFunc];

      (allowMultiAggregate ? selectedRollupsAggFuncs : selectedRollupsAggFuncs.slice(0, 1)).forEach((aggFunc) => {
        rollups[`${aggFunc}_${metric}`] = {
          metric,
          aggregate: aggFunc
        };
      });
    });

    // if a Table is request, we don't need timeseries data so set the limit to 0
    const includeTimeseries = selectedVisualization === 'table' ? 0 : selectedLimitCount;

    const sort = Object.keys(rollups).map((rollup) => ({
      name: rollup,
      direction: selectedSortOrder
    }));

    const query = {
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement,
        dimensions: selectedDimensions,

        metrics: metrics.map((metric) => ({
          name: metric,
          type: selectedTransformation === 'none' ? 'gauge' : 'counter'
        })),

        range: rangeObject,

        // sum, min, max, avg
        merge: { dimensions: selectedMergeDimensions, aggregate: selectedMergeAggregation },

        window: {
          size: selectedWindowSize,
          fn
        },
        rollups,
        sort,
        limit: selectedRollupsLimit,
        includeTimeseries,
        streamingUpdate,
        viz: {
          type: selectedVisualization,
          rollup: selectedVisualizationRollup,
          metric: selectedVisualizationMetric
        }
      }
    };

    // To get raw, non-bucketized metric results, just omit the window section from the query object.
    if (selectedAggFunc === 'none') {
      delete query.kmetrics.window;
    }

    if (!selectedMergeDimensions?.length) {
      delete query.kmetrics.merge;
    }

    // can't send in an empty filters object, so check if we have any set before add to the query
    if (filters && filters.filterGroups.length > 0) {
      query.kmetrics.filters = filters;
    } else {
      delete query.kmetrics.filters;
    }

    if (rollupFilters && rollupFilters.filterGroups.length > 0) {
      query.kmetrics.rollupFilters = rollupFilters;
    } else {
      delete query.kmetrics.rollupFilters;
    }

    if (deckQueryOverrides) {
      query.kmetrics = {
        ...query.kmetrics,
        ...deckQueryOverrides
      };
    }

    return query;
  };

  @computed
  get availableMeasurementOptions() {
    return this.availableMetricsCollection.models
      .filter((m) => m.get('id') !== 0)
      .map((metric) => ({
        value: metric.id,
        label: `${metric.get('measurement')}`,
        ...metric.get()
      }));
  }

  @computed
  get availableLabelMeasurementOptions() {
    return this.availableMetricsCollection.models
      .filter((m) => m.get('id') !== 0)
      .map((metric) => ({
        value: `${metric.get('measurement')}`,
        label: `${metric.get('measurement')}`
      }));
  }

  @computed
  get availableDeviceOptions() {
    if (this.store.$app.isSubtenant) {
      return [];
    }

    return this.store.$devices.nmsDeviceSummaries.map((device) => ({
      ...device,
      name: device.device_name,
      status: device.nms_status,
      agent_id: device.device_kentik_agent_id,
      value: device.id,
      label: device.device_name
    }));
  }

  @computed
  get hasCloudMeasurements() {
    return this.availableMeasurementOptions.some((o) => o.label.startsWith('/cloud'));
  }

  @computed
  get hasAgentMeasurements() {
    return this.availableMetricsCollection.some((o) => o.label.startsWith('/kentik/agent'));
  }

  @computed
  get canAccessMetricsExplorer() {
    return this.availableMetricsCollection.size > 0;
  }

  @computed
  get hasNmsDevices() {
    return (
      !this.store.$app.isSubtenant &&
      (this.store.$app.isSharedLink
        ? this.deviceCollection.hasFetched && this.deviceCollection.unfilteredSize > 0
        : this.store.$devices.hasNmsDevices)
    );
  }

  getMetricOptions = (metric, measurement) => {
    const measurementModel = this.measurementModel(measurement);
    if (!metric || !measurementModel) {
      return [];
    }

    const values = measurementModel.get(`storage.Metrics.${metric}.Values`);

    if (!values) {
      return [];
    }

    return Object.entries(values).map(([key, value]) => ({
      label: getMetricValueLabel(value),
      value: parseInt(key, 10)
    }));
  };

  get interfaceMeasurementId() {
    return this.measurementModel('/interfaces/counters')?.id;
  }

  get deviceMeasurementId() {
    return this.measurementModel('/system')?.id;
  }

  get bgpNeighborsMeasurementId() {
    return this.measurementModel('/protocols/bgp/neighbors')?.id;
  }

  get customMeasurementId() {
    return this.measurementModel('/components')?.id;
  }

  exportDevicesCsv = (deviceCollection = this.deviceCollection) => {
    const { data } = deviceCollection.getFetchOptions();
    this.exportRemoteCsv(data, { type: 'devices' });
  };

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportInterfacesForDeviceCsv = exportInterfacesForDeviceCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportComponentsForDeviceCsv = exportComponentsForDeviceCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportBgpNeighborsForDeviceCsv = exportBgpNeighborsForDeviceCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportQueryResultsCollectionTimeseriesToCsv = exportQueryResultsCollectionTimeseriesToCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportQueryResultsCollectionToCsv = exportQueryResultsCollectionToCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportCsv = exportCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportRemoteCsv = exportRemoteCsv;

  /**
   * @deprecated import directly from metricsExports instead
   */
  exportFileName = exportFileName;

  decorateFilterObj = ({ filterField, filterValue, operator = '=', metric = '', aggregate = '' }) => ({
    filterField,
    filterValue,
    metric,
    aggregate,
    operator
  });

  // convenience fn for composing the link query from Cloud Topology Metrics Sidebar widget
  // gets passed to Metrics Explorer Button
  getCloudTopologyMetricExplorerLinkQuery = ({
    selectedMeasurement,
    metricName,
    selectedVisualizationMetric,
    selectedDimensions,
    selectedFilters,
    ending_time,
    starting_time
  }) => {
    const defaultRollupFilters = {
      connector: 'All',
      filterGroups: [
        {
          name: '',
          named: false,
          connector: 'All',
          not: false,
          autoAdded: '',
          filters: [],
          saved_filters: [],
          filterGroups: []
        }
      ]
    };
    const defaultFilters = {
      connector: 'All',
      filterGroups: [
        {
          name: '',
          named: false,
          connector: 'All',
          not: false,
          autoAdded: '',
          filters: selectedFilters ?? [],
          saved_filters: [],
          filterGroups: []
        }
      ]
    };
    const defaultKmetricsValues = {
      selectedMeasurement,
      selectedMetrics: [metricName],
      selectedDimensions,
      selectedSortOrder: 'desc',
      selectedLimitCount: 20,
      selectedAggFunc: 'avg',
      selectedWindowSize: 0,
      selectedTransformation: 'none',
      selectedRollupsLimit: 100,
      selectedRollupsAggFunc: ['avg', 'max', 'min', 'last', 'p95'],
      selectedVisualization: 'line',
      selectedVisualizationRollup: `last_${metricName}`,
      filters: defaultFilters,
      rollupFilters: defaultRollupFilters,
      ending_time,
      starting_time,
      selectedVisualizationMetric
    };
    return this.formValuesToQuery(defaultKmetricsValues, { allowMultiAggregate: true });
  };
}

const metricsStore = new MetricsStore();

export default metricsStore;
